import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Synchronous idempotency guard — StrictMode fires effects twice in dev.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Missing session id");
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/session`,
          {},
          {
            headers: { "X-Session-ID": sessionId },
            withCredentials: true,
          }
        );
        setUser(data);
        // Clean the URL fragment and go to workspace
        window.history.replaceState({}, "", "/workspace");
        navigate("/workspace", { replace: true, state: { user: data } });
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            "Could not complete sign in. Please try again."
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0D1421] flex items-center justify-center px-6 text-[#EEEEEF]">
      <div className="max-w-md w-full" data-testid="auth-callback-page">
        {!error && (
          <div className="flex items-center gap-3 text-[#EEEEEF]">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
            <span>Completing sign in…</span>
          </div>
        )}
        {error && (
          <div>
            <h1 className="font-editorial text-4xl md:text-5xl font-light tracking-tighter">
              Sign in <span className="italic">failed.</span>
            </h1>
            <p className="mt-6 text-[#C9C6C6]" data-testid="auth-callback-error">
              {error}
            </p>
            <a
              href="/workspace/login"
              className="mt-8 inline-flex text-[#0E93EC] font-medium border-b border-[#0E93EC] pb-1"
            >
              Try again
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
